const { debounce, viewportSize } = window.BAO.Utils
class ProductMediaCarousel extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['slides', 'slide']
  }

  connectedCallback () {
    super.connectedCallback()
    if (this.els?.slide?.elements?.length < 2) return
    if (viewportSize() !== 'xs') return

    this.carousel = this.setupCarousel()
  }

  setupListeners () {
    super.setupListeners()

    if (viewportSize() === 'xs') return

    if (this.els?.media?.exists && this.els?.dot?.exists) {
      this.handleMediaEntry = this.handleMediaEntry.bind(this)
      this.debouncedHandleMediaEntry = debounce(this.handleMediaEntry, 250)

      // eslint-disable-next-line compat/compat
      this.observer = new IntersectionObserver(this.handleMediaEntry, {
        threshold: this.intersectionThreshold,
      })

      this.els.media.elements.forEach(dotEl => {
        this.observer.observe(dotEl)
      })
    }

    if (this.els?.dot?.exists) {
      this.handleDotClick = this.handleDotClick.bind(this)
      this.els.dot.elements.forEach(dotEl =>
        this.listeners.add(dotEl, 'click', this.handleDotClick),
      )
    }
  }

  handleMediaEntry (entries, observer) {
    if (viewportSize() === 'xs') return

    const entry = entries.find(
      entry => entry.intersectionRatio >= observer.thresholds[0],
    )
    if (!entry) return

    const currentIndex = entry?.target?.dataset?.index
    this.els.dot.elements.forEach(dotEl =>
      dotEl.setAttribute(
        'aria-current',
        String(dotEl.dataset?.index === currentIndex),
      ),
    )
  }

  handleDotClick (event) {
    const currentIndex = event?.target?.dataset?.index
    const currentMedia = this.els.media.elements.find(
      mediaEl => mediaEl.dataset?.index === currentIndex,
    )
    if (!currentMedia) return
    this.els.dot.elements.forEach(dotEl =>
      dotEl.setAttribute(
        'aria-current',
        String(dotEl.dataset?.index === currentIndex),
      ),
    )
    if (viewportSize() !== 'xs') {
      currentMedia.scrollIntoView(true)
    }
    event.target.blur()
  }

  setupCarousel () {
    // eslint-disable-next-line no-undef
    const swiper = new Swiper(this.els.slides.element, {
      zoom: {
        maxRatio: 5,
        minRatio: 1,
        toggle: true,
        containerClass: 'swiper-zoom-container',
        zoomedSlideClass: 'swiper-slide-zoomed',
        scale: 1,
        doubleTap: true,
        doubleTapToggle: true,
        pinchToClose: true,
        zoomToSlide: true,
        scaleRatio: 0.5,
        minScale: 1,
        maxScale: 5,
      },
      loop: false,
      pagination: {
        el: this.querySelector('.prd-Media_Dots'),
      },
      touchRatio: 1,
      touchAngle: 45,
      touchMoveStopPropagation: true,
      preventInteractionOnTransition: true,
      touchStartPreventDefault: false,
      touchStartForcePreventDefault: false,
      touchMovePreventDefault: false,
      touchEndPreventDefault: false,
      allowTouchMove: true,
      followFinger: true,
      threshold: 5,
      breakpoints: {
        768: {
          allowTouchMove: false,
          touchRatio: 0,
          zoom: {
            enabled: false,
          },
        },
      },
    })

    if (viewportSize() === 'xs') {
      // Add pointerdown event listener to handle multi-touch
      const zoomContainers = this.querySelectorAll('.swiper-zoom-container')

      zoomContainers.forEach(zoomContainer => {
        let fingerCount = 0

        // Prevent default touch events that might interfere with pinch
        zoomContainer.addEventListener(
          'touchstart',
          e => {
            if (e.touches.length > 1) {
              e.preventDefault()
            }
          },
          { passive: false },
        )

        zoomContainer.addEventListener(
          'touchmove',
          e => {
            if (e.touches.length > 1) {
              e.preventDefault()
            }
          },
          { passive: false },
        )

        // Handle pointer events for more precise control
        zoomContainer.addEventListener('pointerdown', e => {
          fingerCount++
          if (fingerCount === 2) {
            zoomContainer.style.touchAction = 'none'
            // Also prevent default to ensure no browser gestures interfere
            e.preventDefault()
          } else {
            // Allow scrolling with one finger, even when zoomed
            zoomContainer.style.touchAction = 'pan-x pan-y pinch-zoom'
          }
        })

        zoomContainer.addEventListener('pointermove', e => {
          if (fingerCount === 2) {
            e.preventDefault()
          }
        })

        zoomContainer.addEventListener('pointerup', () => {
          fingerCount--
          if (fingerCount < 2) {
            // Always allow scrolling with one finger
            zoomContainer.style.touchAction = 'pan-x pan-y pinch-zoom'
          }
        })

        zoomContainer.addEventListener('pointercancel', () => {
          fingerCount = 0
          // Always allow scrolling with one finger
          zoomContainer.style.touchAction = 'pan-x pan-y pinch-zoom'
        })
      })
    }

    return swiper
  }
}

if (!customElements.get('product-media-carousel')) {
  customElements.define('product-media-carousel', ProductMediaCarousel)
}
